import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Nav, Tab, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine, faCalendarAlt, faSync, faDownload,
    faUsers, faTachometerAlt, faArrowTrendUp, faClock, faTimes, faChartArea
} from '@fortawesome/free-solid-svg-icons';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { ApiManager } from '../../core/api-manager';
import { LoginManager } from '../../core/login-manager';
import { settings } from '../../config/settings';
import { toast } from 'react-toastify';

// Import dei componenti tab
import OverviewTab from './components/OverviewTab';
import TrendsTab from './components/TrendsTab';
import SessionsTab from './components/SessionsTab';
import PerformanceTab from './components/PerformanceTab';
import ActivityTab from './components/ActivityTab';

import './style.css';

// Registra i componenti di Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

export default function Analytics() {
    const [userInfo, setUserInfo] = useState(LoginManager.getUserInfo());
    const [canViewAnalytics, setCanViewAnalytics] = useState(false);
    // Stati principali
    const [activeTab, setActiveTab] = useState('overview');
    const [timeRange, setTimeRange] = useState('7d');
    const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
    const [availableEntities, setAvailableEntities] = useState([]);
    const [refreshing, setRefreshing] = useState(false);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        loadEntities();
    }, []);


    useEffect(() => {
        if (userInfo !== null && settings.advancedAnalytics && Array.isArray(settings.advancedAnalytics)) {
            setCanViewAnalytics(settings.advancedAnalytics.includes(parseInt(userInfo.level)))
        }
        else {
            setCanViewAnalytics(false)
        }
    }, [userInfo]);


    const loadEntities = async () => {
        try {
            const response = await ApiManager.sendAuthenticatedRequest('/analytics/overview', {
                timeRange: '7d'
            });

            const overviewData = response?.data || response;
            if (overviewData?.availableEntities) {
                setAvailableEntities(overviewData.availableEntities);
            }
        } catch (error) {
            console.error('Error loading entities:', error);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        const startTime = Date.now();

        try {
            // Forza il refresh di tutte le tab triggerando un re-render
            const currentTab = activeTab;
            setActiveTab(''); // Resetta temporaneamente

            // Ricarica le entità
            await loadEntities();

            // Piccolo delay per permettere ai componenti di aggiornarsi
            setTimeout(() => {
                setActiveTab(currentTab); // Ripristina la tab attiva
            }, 100);

        } catch (error) {
            console.error('Error refreshing data:', error);
            toast.error('Errore nel refresh dei dati');
        } finally {
            // Calcola il tempo trascorso e assicura un delay minimo di 2 secondi
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 2000 - elapsedTime);

            setTimeout(() => {
                setRefreshing(false);
            }, remainingTime);
        }
    };

    // Scarica log CSV
    const exportLogs = async () => {
        setExporting(true);
        try {
            // Prepara i parametri per l'API
            const apiParams = { timeRange };
            if (timeRange === 'custom' && customDateRange.start && customDateRange.end) {
                apiParams.startDate = customDateRange.start;
                apiParams.endDate = customDateRange.end;
            }

            const response = await ApiManager.sendAuthenticatedRequest('/analytics/export', apiParams);
            const responseData = response?.data || response;

            if (responseData?.content && responseData?.filename) {
                // Converti il base64 in blob
                const byteCharacters = atob(responseData.content);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: responseData.mimeType });

                // Crea un link temporaneo per il download
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = responseData.filename;
                document.body.appendChild(link);
                link.click();

                // Pulisci
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);

                toast.success('Log scaricati con successo');
            } else {
                throw new Error('Risposta non valida dal server');
            }
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Errore nel download dei log');
        } finally {
            setExporting(false);
        }
    };

    // Configurazione grafici
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    color: document.documentElement.classList.contains('dark-theme-active') ? '#e0e0e0' : '#333'
                }
            },
            tooltip: {
                mode: 'index',
                intersect: false
            }
        },
        scales: {
            x: {
                grid: {
                    color: document.documentElement.classList.contains('dark-theme-active') ? '#404040' : '#e0e0e0'
                },
                ticks: {
                    color: document.documentElement.classList.contains('dark-theme-active') ? '#b0b0b0' : '#666'
                }
            },
            y: {
                grid: {
                    color: document.documentElement.classList.contains('dark-theme-active') ? '#404040' : '#e0e0e0'
                },
                ticks: {
                    color: document.documentElement.classList.contains('dark-theme-active') ? '#b0b0b0' : '#666'
                }
            }
        }
    };

    return (
        <Container fluid className='mt-5 mb-5 analytics-container'>
            <h3>Analytics</h3>
            {canViewAnalytics === true ? (
                <Container fluid className='mt-3'>
                    {/* Header Controls */}
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="d-flex gap-2">
                            {/* Filtro temporale */}
                            <Form.Select
                                value={timeRange}
                                onChange={(e) => setTimeRange(e.target.value)}
                                style={{ width: 'auto' }}
                            >
                                <option value="24h">Ultime 24 ore</option>
                                <option value="7d">Ultimi 7 giorni</option>
                                <option value="30d">Ultimi 30 giorni</option>
                                <option value="custom">Personalizzato</option>
                            </Form.Select>

                        </div>

                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={handleRefresh}
                                disabled={refreshing}
                                title="Aggiorna dati"
                            >
                                {refreshing ? (
                                    <>
                                        <div className="spinner-border spinner-border-sm me-1" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        Aggiornamento...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faSync} className="me-1" /> Aggiorna
                                    </>
                                )}
                            </Button>
                            <Button
                                variant="outline-success"
                                size="sm"
                                onClick={exportLogs}
                                disabled={exporting}
                                title="Scarica log del periodo in formato CSV"
                            >
                                {exporting ? (
                                    <>
                                        <div className="spinner-border spinner-border-sm me-1" role="status">
                                            <span className="visually-hidden">Scaricamento...</span>
                                        </div>
                                        Scaricamento...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faDownload} className="me-1" /> Scarica Log
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Date range personalizzato */}
                    {timeRange === 'custom' && (
                        <div className="row mb-3">
                            <div className="col-md-4">
                                <Form.Group>
                                    <Form.Label>Data inizio</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={customDateRange.start}
                                        onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-4">
                                <Form.Group>
                                    <Form.Label>Data fine</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={customDateRange.end}
                                        onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                                    />
                                </Form.Group>
                            </div>
                        </div>
                    )}

                    {/* Tabs navigazione */}
                    <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                        <Nav variant="tabs" className="mb-4">
                            <Nav.Item>
                                <Nav.Link eventKey="overview">
                                    <FontAwesomeIcon icon={faTachometerAlt} className="me-2" />
                                    Overview
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="trends">
                                    <FontAwesomeIcon icon={faArrowTrendUp} className="me-2" />
                                    Trends
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="sessions">
                                    <FontAwesomeIcon icon={faUsers} className="me-2" />
                                    Sessioni
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="activity">
                                    <FontAwesomeIcon icon={faClock} className="me-2" />
                                    Attività
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="performance">
                                    <FontAwesomeIcon icon={faChartLine} className="me-2" />
                                    Performance
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>

                        <Tab.Content>
                            <Tab.Pane eventKey="overview">
                                <OverviewTab
                                    timeRange={timeRange}
                                    customDateRange={customDateRange}
                                    chartOptions={chartOptions}
                                />
                            </Tab.Pane>

                            <Tab.Pane eventKey="trends">
                                <TrendsTab
                                    timeRange={timeRange}
                                    customDateRange={customDateRange}
                                    chartOptions={chartOptions}
                                    availableEntities={availableEntities}
                                />
                            </Tab.Pane>

                            <Tab.Pane eventKey="sessions">
                                <SessionsTab
                                    timeRange={timeRange}
                                    customDateRange={customDateRange}
                                    chartOptions={chartOptions}
                                />
                            </Tab.Pane>

                            <Tab.Pane eventKey="activity">
                                <ActivityTab
                                    timeRange={timeRange}
                                    customDateRange={customDateRange}
                                />
                            </Tab.Pane>

                            <Tab.Pane eventKey="performance">
                                <PerformanceTab />
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </Container>
            ) : (
                <></>
            )}

        </Container>
    );
};